import { BISHOP, KING, KNIGHT, pieceColor, pieceType } from "../board";
import type { Position } from "../position";
import { isInCheck } from "./attacks";

export type GameStatus =
  | "playing"
  | "check"
  | "checkmate"
  | "stalemate"
  | "draw-fifty-move"
  | "draw-repetition"
  | "draw-material";

/**
 * Repetition key: the position minus the clocks. Two positions repeat when the
 * placement, side to move, castling rights and en passant target all match.
 */
export function repetitionKey(pos: Position): string {
  // Occupied squares only, and **sorted**: the board is a Map now, whose
  // iteration order is the order pieces happened to be inserted in. Two
  // clients replaying one log would agree on that by luck rather than by rule,
  // and a key that depended on it would make repetition detection a desync.
  const squares = [...pos.board.keys()].sort((a, b) => a - b);

  let key = "";
  for (const sq of squares) {
    key += `${sq.toString(36)}:${(pos.board.get(sq) as number).toString(36)}`;
    // Facing is part of the position: two boards with identical pieces but a
    // pawn pointing differently are different positions, because different
    // moves are available. Leaving it out would let a re-aim "repeat" a
    // position it has actually changed.
    const facing = pos.facing.get(sq);
    if (facing) key += `.${facing}`;
    // And what it has *copied*, for the same reason again: two boards with the
    // same pieces on the same squares are different positions when one of them
    // holds a piece that moves like something else.
    const borrowed = pos.imitating.get(sq);
    if (borrowed) key += `.i${borrowed}`;
    // And what it was crowned from: a crowned royal and a born one are both
    // `KING` here and only one slides. Appended only when set.
    const crown = pos.crowned.get(sq);
    if (crown) key += `.c${crown}`;
    // And how hurt it is, and how tough it is (§27). Two boards with the same
    // pieces on the same squares are different positions when one of them
    // holds a piece a blow would finish and the other does not — health
    // decides whether a capture is a capture, so it decides which moves
    // exist. Without this, three blows and a shuffle is a threefold draw.
    // Appended only when set, so every board nobody has hit keys as it did.
    const wounds = pos.hurt.get(sq);
    if (wounds) key += `.h${wounds}`;
    const tough = pos.vitality.get(sq);
    if (tough) key += `.v${tough}`;
    // And its armor: grinding a plate down is progress, not a repetition.
    const plate = pos.plating.get(sq);
    if (plate) key += `.p${plate}`;
    key += ",";
  }
  // And who is about to lose a turn. Two boards with the same pieces on the
  // same squares are different positions when the clock is about to step over
  // somebody on one of them — the same argument facing and a borrowed pattern
  // make, one level up from the pieces. Appended and only when somebody is
  // missing one, so every board keys exactly as it did before this existed.
  // `?? 0`, because a run restored from a snapshot written before this existed
  // has no such field and `sundefined` in a key is a position that repeats with
  // nothing, including itself.
  const skips = (pos.skipNext ?? 0) === 0 ? "" : `|s${pos.skipNext}`;
  // And who is clamped to whom, the same argument again: a clamp decides which
  // moves exist, so a board with one up does not repeat one without it.
  const clamp = pos.clasp
    ? `|k${Math.min(pos.clasp.a, pos.clasp.b)},${Math.max(pos.clasp.a, pos.clasp.b)}`
    : "";
  return `${key}|${pos.turn}|${pos.castling}|${pos.ep}${skips}${clamp}`;
}

/**
 * King versus king, and king+minor versus king, cannot be mated by any legal
 * sequence. Deliberately does not chase the exotic cases (KBvKB same colour,
 * KNNvK) — this is a prototype, and PLAN §3.7 replaces the drawing rules with
 * an industrial clock anyway.
 */
export function hasInsufficientMaterial(pos: Position): boolean {
  const minors = [0, 0];
  let others = 0;

  // A side's *first* king is the piece this rule is about not counting. Any
  // beyond it are ordinary material — a spare king is capturable, moves and
  // fights, so a board of king-plus-spare against a lone king is a game
  // somebody can still win and must not read as a dead draw.
  const crowns = new Int32Array(pos.sides);
  for (const piece of pos.board.values()) {
    const type = pieceType(piece);
    if (type === KING && crowns[pieceColor(piece)]++ === 0) continue;
    if (type === BISHOP || type === KNIGHT) minors[pieceColor(piece)]++;
    else others++;
  }

  return others === 0 && minors[0] + minors[1] <= 1;
}

/**
 * @param draws Whether the three chess draws apply. They do in a game of
 *   chess, where running out of ways to mate is the end of the story. They do
 *   **not** in a run, whose win condition is the level's objectives — and
 *   where all three fire routinely on positions that are going perfectly well:
 *   beat an army down to nothing and both sides can be left with king and a
 *   minor; an industrial phase is fifty quiet moves by design; a king shuffling
 *   beside a heap while the belts run repeats the position on purpose. A drawn
 *   game also refuses construction, so any of them would soft-lock the run with
 *   its objectives still outstanding. Mate and stalemate still apply either
 *   way: a player who cannot move has been beaten, and it is what a `defeat`
 *   objective watches for.
 */
export function gameStatus(
  pos: Position,
  legalMoveCount: number,
  repetitions: ReadonlyMap<string, number>,
  draws = true,
  /**
   * Does a side that lost its last king play on (§31 K6 grace, `kinglessGrace`)?
   * Then the despair clock ends it, not this; false is the old instant loss.
   */
  grace = false
): GameStatus {
  const inCheck = isInCheck(pos, pos.turn);

  // **An army whose king has been taken is beaten.**
  //
  // Reachable since belts began carrying kings: two kings can never be
  // adjacent by moving, but a lane walks one up to the other, and whoever is
  // on the clock then takes (`canTake`). The side left without a king has no
  // king to be in check, so every test below reads it as *stalemate* — a draw,
  // for a side that has just lost its king.
  //
  // Gated on somebody else still having one, so a board with no kings at all
  // is untouched: that is most of the industry fixtures, and they are not
  // games anybody has lost.
  if (
    pos.kings[pos.turn] < 0 &&
    pos.kings.some((sq, side) => sq >= 0 && side !== pos.turn) &&
    !(grace && pos.counts[pos.turn] > 0)
  ) {
    return "checkmate";
  }

  if (legalMoveCount === 0) return inCheck ? "checkmate" : "stalemate";
  if (draws) {
    if (hasInsufficientMaterial(pos)) return "draw-material";
    if (pos.halfmove >= 100) return "draw-fifty-move";
    if ((repetitions.get(repetitionKey(pos)) ?? 0) >= 3) return "draw-repetition";
  }

  return inCheck ? "check" : "playing";
}

export const isGameOver = (status: GameStatus): boolean =>
  status !== "playing" && status !== "check";
